import { useMemo } from 'react';
import { withBasePath } from '../basePath';
import styled from 'styled-components';
import { BodyContainer } from 'atomicComponents/Layout/BodyContainer';
import { ChevronIcon } from '@ardoq/icons';
import { MetamodelVisualization } from '@ardoq/metamodel-visualization';
import { Stats } from './metamodelStats/Stats';
import { t, type I18nLocale } from '@ardoq/i18n';
import { type Locale } from '@ardoq/locale';
import { getSimpleBlockDiagramLabels } from 'locales/getSimpleBlockDiagramLabels';
import {
  OpeningMethod,
  trackClickCloseMetamodelStatsButton,
  trackClickOpenMetamodelStats,
} from './tracking';
import { MetamodelContext, SelectedMetamodelItem } from './types';
import { dispatchAction } from '@ardoq/rxbeach';
import * as profiling from '@ardoq/profiling';
import type { GraphComponent } from '@ardoq/yfiles';
import {
  selectMetamodelItem,
  setMetamodelInfoContainerIsOpen,
} from './navigation/actions';
import { colors } from '@ardoq/design-tokens';
import { APIFieldAttributes, PersistedMetamodel } from '@ardoq/api-types';
import { ArdoqLoaderSpinner, LoaderColor, LoaderSize } from '@ardoq/loader';
import { FlexBox } from '@ardoq/layout';

const ViewContainer = styled.div`
  position: relative;
  height: 100%;
  overflow-x: hidden;
`;

interface InfoContainerStyleProps {
  $infoContainerIsOpen: boolean;
  $infoContainerWidth: number;
}

const VisualizationContainer = styled.div<InfoContainerStyleProps>`
  position: absolute;
  height: 100%;
  width: 100%;
  overflow: hidden;
  background: white;
  transition: 0.5s;
  transform: ${({ $infoContainerIsOpen, $infoContainerWidth }) =>
    `translateX(${$infoContainerIsOpen ? -$infoContainerWidth / 2 : 0}px)`};
`;

const ExpandableSidebar = styled(({ ...args }) => (
  <BodyContainer {...args} />
))<InfoContainerStyleProps>`
  position: absolute;
  width: ${({ $infoContainerWidth }) => $infoContainerWidth}px;
  right: ${({
    $infoContainerIsOpen,
    $infoContainerWidth,
  }: InfoContainerStyleProps) =>
    $infoContainerIsOpen ? 0 : -$infoContainerWidth}px;
  padding: 32px;
  transition: 0.5s;
`;

const CloseStatsButton = styled(ChevronIcon)<InfoContainerStyleProps>`
  color: ${colors.grey400};
  position: absolute;
  right: ${({
    $infoContainerIsOpen,
    $infoContainerWidth,
  }: InfoContainerStyleProps) =>
    $infoContainerIsOpen ? '32px' : `${-$infoContainerWidth + 32}px`};
  transition: 0.5s;
  z-index: 2;
  font-size: 24px;
  top: 32px;
  cursor: pointer;
`;

const OpenStatsButtonWrapper = styled.div<InfoContainerStyleProps>`
  position: absolute;
  top: 20px;
  right: ${({ $infoContainerIsOpen, $infoContainerWidth }) =>
    $infoContainerIsOpen ? $infoContainerWidth - 106 : -15}px;
  transition: 0.5s;
  cursor: pointer;
  &:hover {
    transform: translate(-15px, 0);
  }
`;

interface InfoContainerProps {
  infoContainerIsOpen: boolean;
  infoContainerWidth: number;
}

const OpenStatsButton = ({
  onClick,
  infoContainerIsOpen,
  infoContainerWidth,
  i18nLocale,
}: {
  onClick: VoidFunction;
  i18nLocale: I18nLocale;
} & InfoContainerProps) => (
  <OpenStatsButtonWrapper
    $infoContainerIsOpen={infoContainerIsOpen}
    $infoContainerWidth={infoContainerWidth}
  >
    <img
      data-tooltip-text={t(
        i18nLocale,
        'metamodel:view.showAnalytics',
        'Show Metamodel analytics'
      )}
      onClick={onClick}
      src={withBasePath('/img/svg-icons/icon_ardoq_stats.svg')}
    />
  </OpenStatsButtonWrapper>
);

const onItemSelection = (selectedItem: SelectedMetamodelItem) => {
  dispatchAction(selectMetamodelItem(selectedItem));
};

type MetamodelViewProps = {
  metamodel: PersistedMetamodel;
  context: MetamodelContext;
  infoContainerIsOpen: boolean;
  infoContainerWidth: number;
  isCheckboxField: boolean;
  nonTemplateFieldsByName: Map<string, APIFieldAttributes>;
  registerVisualizationContainer?: (
    visualizationContainer: HTMLElement
  ) => void;
  registerGraphComponent?: (graphComponent: GraphComponent) => void;
  unregisterVisualizationContainer?: () => void;
  unregisterGraphComponent?: () => void;
  animateLayout?: boolean;
  locale: Locale;
  i18nLocale: I18nLocale;
};

const MetamodelView = ({
  metamodel,
  context,
  infoContainerIsOpen,
  infoContainerWidth,
  isCheckboxField,
  nonTemplateFieldsByName,
  registerVisualizationContainer,
  registerGraphComponent,
  unregisterVisualizationContainer,
  unregisterGraphComponent,
  animateLayout,
  locale,
  i18nLocale,
}: MetamodelViewProps) => {
  profiling.useTransaction(
    'metamodel view render',
    1000,
    {
      viewId: 'metamodel',
      metadata: {
        compTypes: Object.keys(metamodel.metamodel.componentTypes).length,
        refTypes: Object.keys(metamodel.metamodel.referenceTypes).length,
      },
    },
    profiling.Team.ENGAGEMENT
  );
  const simpleBlockDiagramLabels = useMemo(
    () => getSimpleBlockDiagramLabels(i18nLocale),
    [i18nLocale]
  );
  return (
    <ViewContainer>
      <VisualizationContainer
        $infoContainerIsOpen={infoContainerIsOpen}
        $infoContainerWidth={infoContainerWidth}
      >
        <MetamodelVisualization
          metamodelComponents={metamodel.metamodel.componentTypes}
          metamodelReferences={metamodel.metamodel.referenceTypes}
          metamodelWorkspaces={metamodel.metamodel.workspaces}
          metamodelAssetFolders={metamodel.metamodel.workspaceFolders}
          loadingAsset={
            <FlexBox
              justify="center"
              width="full"
              marginTop="xxlarge"
              paddingTop="xxlarge"
            >
              <ArdoqLoaderSpinner
                color={LoaderColor.GRAY}
                size={LoaderSize.MEDIUM}
              />
            </FlexBox>
          }
          onItemFocus={onItemSelection}
          registerVisualizationContainer={registerVisualizationContainer}
          registerGraphComponent={registerGraphComponent}
          unregisterVisualizationContainer={unregisterVisualizationContainer}
          unregisterGraphComponent={unregisterGraphComponent}
          animateLayout={animateLayout}
          hasLeftMenu={true}
          labels={simpleBlockDiagramLabels}
        />
      </VisualizationContainer>
      <OpenStatsButton
        infoContainerIsOpen={infoContainerIsOpen}
        infoContainerWidth={infoContainerWidth}
        onClick={() => {
          trackClickOpenMetamodelStats(OpeningMethod.METAMODEL_STATS_BUTTON);
          dispatchAction(setMetamodelInfoContainerIsOpen(true));
        }}
        i18nLocale={i18nLocale}
      />
      <CloseStatsButton
        direction="right"
        $infoContainerIsOpen={infoContainerIsOpen}
        $infoContainerWidth={infoContainerWidth}
        deprecated_onClick={() => {
          trackClickCloseMetamodelStatsButton();
          dispatchAction(setMetamodelInfoContainerIsOpen(false));
        }}
      />
      <ExpandableSidebar
        $infoContainerIsOpen={infoContainerIsOpen}
        $infoContainerWidth={infoContainerWidth}
      >
        <Stats
          context={context}
          metamodel={metamodel}
          isCheckboxField={isCheckboxField}
          nonTemplateFieldsByName={nonTemplateFieldsByName}
          locale={locale}
          i18nLocale={i18nLocale}
        />
      </ExpandableSidebar>
    </ViewContainer>
  );
};

export { MetamodelView };
